import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import {
  AlertTriangle,
  Ban,
  Check,
  Clock,
  Copy,
  Handshake,
  Mail,
  MailWarning,
  PhoneCall,
  SlidersHorizontal,
  Undo2,
} from "lucide-react"

import { BotonComentario } from "@/components/Comentarios"
import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { ReasonDialog } from "@/components/ReasonDialog"
import { Toolbar, ToolbarField, ToolbarNote } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { formatAmount, formatDate } from "@/lib/format"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { refFactura } from "@/routes/comentarios/api"
import { useFinanceFilters } from "@/routes/finance/context"
import { nombrePais } from "@/routes/finance/mapping"
import {
  declararIncobrable,
  enviarSeguimiento,
  fetchCartera,
  marcarEnGestion,
  quitarDeGestion,
  revertirIncobrable,
  type Deudor,
  type Orden,
  type ResultadoSeguimiento,
} from "@/routes/cobranza/api"
import { CadenciaDialog } from "@/routes/cobranza/CadenciaDialog"
import { PlantillasDialog } from "@/routes/cobranza/PlantillasDialog"
import { t } from "@/lib/i18n"

/**
 * COBRANZAS: quién debe, qué toca hoy y mandarlo con un clic.
 *
 * Punto 6 de Cesar. La cadencia es suya, literal: «una semana antes de
 * vencimiento, un día antes, la fecha de vencimiento y luego cada martes o
 * jueves; y si no, que escale al gerente para que llame».
 *
 * ---------------------------------------------------------------------------
 * TRES COSAS QUE LA PANTALLA TIENE QUE DECIR Y NO SON OBVIAS
 *
 * **1. El vencimiento está CALCULADO.** `invoice` no lo guarda: sale de la
 * fecha de emisión más los días de crédito de la política. Quien mire la
 * columna tiene que saberlo, porque cambiar la política mueve todas las fechas
 * de golpe y eso parece un error si no se sabe que es la regla.
 *
 * **2. «Probable incobrable» NO es «incobrable».** Lo primero es un umbral de
 * días que dice «esto ya hay que mirarlo». Lo segundo es una decisión contable
 * —lleva provisión— que toma una persona con su motivo. Se pintan distinto y
 * se llaman distinto a propósito: confundirlos es cómo un sistema acaba dando
 * de baja una deuda que nadie decidió dar de baja.
 *
 * **4. EL VOCABULARIO SE DICE POR SU EFECTO, no por el mecanismo.** Cesar, punto
 * 8 de la tercera tanda: «no me queda claro en cobranzas qué se refiere en
 * gestión, y que esté marcado como incobrable, y cuando le doy click en gestión
 * ¿qué pasa? ¿Se pone volver a la cadena? ¿Qué es eso?».
 *
 * Tenía razón, y el defecto era de lenguaje. «En gestión» y «volver a la
 * cadencia» están escritos desde DENTRO del sistema: nombran la cadencia —la
 * cola de recordatorios automáticos— que es una pieza que quien cobra no ve y
 * no tiene por qué conocer. Lo único que esos dos botones hacen, visto desde
 * fuera, es **parar los avisos automáticos y volver a arrancarlos**, y eso es lo
 * que ahora dicen.
 *
 * Lo que NO se renombró es «incobrable», y no por descuido: es el término
 * contable correcto, lleva provisión y en varios países exige haber gestionado
 * el cobro antes. Cambiarlo por «perdida» o «dada de baja» haría más legible la
 * pantalla y menos exacta la contabilidad. Lo que se añade es la CONSECUENCIA al
 * lado, que es lo que de verdad faltaba: la palabra se entiende, lo que no se
 * sabía era qué pasa al pulsarla.
 *
 * **3. Lo que no se envió NO cuenta como reclamado.** Si el servidor no tiene
 * correo configurado, o el cliente no tiene dirección, el texto se redacta y se
 * puede copiar — pero la factura sigue apareciendo como pendiente. Un
 * «reclamado el 17 de marzo» falso hace que alguien deje de llamar a un cliente
 * al que nadie escribió.
 */
export function CobranzasPage() {
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const { toast } = useToast()
  const qc = useQueryClient()

  /*
    LA SOCIEDAD LA MANDA LA BARRA DE ARRIBA, como en el resto de Finanzas.

    Cobranzas vive dentro de `FinanceLayout`, asi que hereda su alcance. Antes
    tenia su PROPIO desplegable que arrancaba en «Todas»: se entraba a reclamar
    facturas de las cuatro sociedades mientras arriba ponia una. Y aqui eso no
    es solo leer mal — desde esta pantalla se MANDAN correos. Ver `lib/alcance.ts`.
  */
  const { paisQuery, listo } = useFinanceFilters()
  const [orden, setOrden] = React.useState<Orden>("vencimiento")
  const [soloHoy, setSoloHoy] = React.useState(false)
  /** Mes de VENCIMIENTO («AAAA-MM»), o todos. Cesar: «debería filtrar también por mes de vencimiento». */
  const [mesVencimiento, setMesVencimiento] = React.useState<string>("todos")
  const [marcadas, setMarcadas] = React.useState<Record<string, boolean>>({})
  const [resultados, setResultados] = React.useState<ResultadoSeguimiento[] | null>(null)
  const [aIncobrable, setAIncobrable] = React.useState<Deudor | null>(null)
  const [plantillasAbiertas, setPlantillasAbiertas] = React.useState(false)
  const [cadenciaAbierta, setCadenciaAbierta] = React.useState(false)

  const cartera = useQuery({
    queryKey: ["cobranza-deudores", paisQuery, orden],
    queryFn: () => fetchCartera({ pais: paisQuery, orden }),
    // Sin sociedad elegida no se lista cartera: desde aqui se mandan correos de
    // reclamo, y reclamar la factura de otra sociedad es peor que no verla.
    enabled: listo,
  })

  const mesesDeVencimiento = React.useMemo(() => {
    const vistos = new Set<string>()
    for (const d of cartera.data?.deudores ?? []) {
      if (d.fecha_vencimiento) vistos.add(d.fecha_vencimiento.slice(0, 7))
    }
    return [...vistos].sort()
  }, [cartera.data])

  const filas = React.useMemo(() => {
    const todos = cartera.data?.deudores ?? []
    const porMes = mesVencimiento === "todos"
      ? todos
      : todos.filter((d) => (d.fecha_vencimiento ?? "").slice(0, 7) === mesVencimiento)
    return soloHoy ? porMes.filter((d) => d.toca_hoy) : porMes
  }, [cartera.data, soloHoy, mesVencimiento])

  const seleccionadas = filas.filter((d) => marcadas[d.id])

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ["cobranza-deudores"] })
  }

  const enviar = useMutation({
    mutationFn: () =>
      enviarSeguimiento({
        invoice_ids: seleccionadas.map((d) => d.id),
        /*
          SIN DESTINATARIOS. El backend los resuelve desde la ficha del cliente
          (`party.email_cobranza`). Este mapa sigue existiendo en la API para el
          caso de «esta factura mándasela al gerente», que es una escalada y no
          el camino normal — pero ya no es donde se teclea el correo cada vez.
        */
        destinatarios: {},
      }),
    onSuccess: (r) => {
      setResultados(r.resultados)
      setMarcadas({})
      toast({
        variant: r.enviados === r.resultados.length ? "success" : "warning",
        title: `${r.enviados} de ${r.resultados.length} enviado(s)`,
        description: r.aviso ?? undefined,
      })
      invalidar()
    },
    onError: onWriteError("el seguimiento", "enviar"),
  })

  const marcarIncobrable = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      declararIncobrable(id, motivo),
    onSuccess: (r) => {
      toast({ title: "Declarada incobrable", description: r.aviso })
      setAIncobrable(null)
      invalidar()
    },
    onError: onWriteError("la factura", "declarar incobrable"),
  })

  const revertir = useMutation({
    mutationFn: revertirIncobrable,
    onSuccess: () => {
      toast({ title: "Vuelve a la cartera por cobrar" })
      invalidar()
    },
    onError: onWriteError("la factura", "reactivar"),
  })

  /*
    EN GESTIÓN, A UN CLIC Y SIN PEDIR MOTIVO.

    El incobrable exige diez caracteres de motivo porque lleva provisión
    contable. Éste no exige nada, y es deliberado: si marcar «estoy hablando
    con ellos» costara rellenar un formulario, nadie lo marcaría — y sin la
    marca el sistema le manda el recordatorio automático al cliente con el que
    se acaba de acordar un plan de pagos.

    La nota se añade después, desde la misma fila, para quien quiera dejarla.
  */
  const gestionar = useMutation({
    mutationFn: ({ id, nota }: { id: string; nota?: string }) =>
      marcarEnGestion(id, nota),
    onSuccess: (r) => {
      toast({ title: t("Avisos en pausa"), description: r.aviso })
      invalidar()
    },
    onError: onWriteError("la factura", "poner en gestión"),
  })

  const soltar = useMutation({
    mutationFn: quitarDeGestion,
    onSuccess: (r) => {
      toast({ title: t("Avisos reanudados"), description: r.aviso })
      invalidar()
    },
    onError: onWriteError("la factura", "sacar de gestión"),
  })

  const tramos = cartera.data?.por_tramo ?? {}

  return (
    <div className="space-y-4">
      {/*
        SIN CABECERA PROPIA. `FinanceLayout` ya pinta la de la sección, y las
        otras ocho pestañas tampoco ponen la suya: dos titulares seguidos
        —«Modelo de gestión.» y «Cobranzas.»— son dos puntos rojos en la misma
        pieza y un nivel de jerarquía de más. Lo que esta pantalla necesita
        decir de sí misma cabe en el pie del panel.
      */}
      {/*
        CINCO COLUMNAS Y NO LAS CUATRO POR DEFECTO. Con `lg:grid-cols-4` la
        quinta tarjeta caía sola en una segunda fila y se leía como un bloque
        aparte —como si «los avisos en pausa» no fueran del mismo grupo que las otras
        cuatro—. Se vio en una captura, no en un test.
      */}
      {/*
        SIN SOCIEDAD ELEGIDA NO SE ENSEÑAN CINCO CEROS.

        `cartera.data` es `undefined` mientras la consulta está desactivada, y
        las tarjetas caían al 0 por defecto: cinco ceros encima de un mensaje
        que dice «elige una sociedad» se leen como «esta empresa no debe
        nada». Es el mismo `null` con dos significados de siempre — «aún no lo
        sé» presentado como «cero».
      */}
      {listo ? (
      <MetricRow className="lg:grid-cols-5">
        <MetricCard label={t("Por cobrar")} value={cartera.data?.total_usd ?? 0}
                    loading={cartera.isLoading} />
        <MetricCard label={t("Vencido +90 días")}
                    value={tramos["+90"]?.importe_usd ?? 0}
                    loading={cartera.isLoading}
                    /*
                      EL ÚNICO ROJO DE LA VISTA. Lámina 18: una cifra en rojo por
                      pantalla, la que requiere atención. En una cartera, eso es
                      lo que lleva más de noventa días: lo demás es el trabajo
                      normal de cobrar.
                    */
                    tono={(tramos["+90"]?.importe_usd ?? 0) > 0 ? "alerta" : "neutro"} />
        <MetricCard label={t("Les toca hoy")} value={cartera.data?.con_toque_hoy ?? 0}
                    loading={cartera.isLoading} integer />
        {/*
          «CANDIDATAS A PROVISIÓN» Y NO «PROBABLES INCOBRABLES». Es un umbral de
          días, no una decisión de nadie, y el rótulo anterior se leía como si el
          sistema ya hubiera concluido algo. Dice qué hay que decidir, no qué se
          decidió — y no pone el número de días en el rótulo porque el umbral sale
          de la política de cobranza y se puede cambiar.
        */}
        <MetricCard label={t("Candidatas a provisión")}
                    value={cartera.data?.probables_incobrables ?? 0}
                    loading={cartera.isLoading} integer />
        {/*
          LO PAUSADO VA EN IMPORTE Y NO EN RECUENTO, al revés que las dos de su
          izquierda. «Tres facturas en gestión» no dice nada; «USD 48.000 en
          gestión» dice cuánta plata depende de que esas conversaciones
          terminen bien, que es la pregunta que se hace al mirar esta fila.
        */}
        <MetricCard label={t("Con los avisos en pausa")}
                    value={cartera.data?.en_gestion_usd ?? 0}
                    loading={cartera.isLoading} />
      </MetricRow>
      ) : null}

      <Toolbar>
        <ToolbarField label={t("Ordenar por")} htmlFor="cob-orden">
          <Select value={orden} onValueChange={(v) => setOrden(v as Orden)}>
            <SelectTrigger id="cob-orden" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(cartera.data?.ordenes ?? {
                vencimiento: "la más vencida primero",
              }).map(([id, ayuda]) => (
                <SelectItem key={id} value={id} title={ayuda}>
                  {id === "vencimiento" ? "Fecha de vencimiento"
                    : id === "monto" ? "Monto"
                    : id === "riesgo" ? "Riesgo de no cobrarse"
                    : "Cliente"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        <ToolbarField label={t("Vence en")} htmlFor="cob-mes">
          <Select value={mesVencimiento} onValueChange={setMesVencimiento}>
            <SelectTrigger id="cob-mes" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">{t("Cualquier mes")}</SelectItem>
              {mesesDeVencimiento.map((m) => (
                <SelectItem key={m} value={m}>{nombreDelMes(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        <label className="flex cursor-pointer items-center gap-1.5 self-end pb-1 text-xs">
          <Checkbox checked={soloHoy} onCheckedChange={(v) => setSoloHoy(Boolean(v))} />
          {t("Sólo las que tocan hoy")}</label>

        <ToolbarNote icon={Clock} className="ml-auto">
          {cartera.data
            ? `${filas.length} factura(s) · al ${formatDate(cartera.data.hoy)}`
            : "—"}
        </ToolbarNote>
      </Toolbar>

      {/*
        LA CADENCIA, DICHA.

        Cesar: «esas reglas de cobranza, ¿en dónde se modifican? No queda claro».

        La cadencia —cuándo toca cada aviso— decide qué sale en la columna «Qué
        toca» de cada fila, así que se resume aquí, encima de la cartera. Hubo un
        enlace a Maestros › Políticas para cambiarla, y se quitó con la pestaña:
        aquel formulario guardaba la cadencia con unas claves y el motor leía
        otras, así que cambiarla allí no cambiaba ningún aviso. El formulario
        que SÍ la cambia es `CadenciaDialog`, y vive aquí: se cambia la regla
        mirando la cartera a la que se le aplica.
      */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-dashed px-2.5 py-1.5">
        {/*
          «CUÁNDO AVISO» Y NO «CADENCIA». Misma razón que los botones de la fila:
          la cadencia es el mecanismo y nadie de fuera lo llama así. El rótulo
          tiene que decir qué contesta esta tira.
        */}
        <span className="eyebrow text-muted-foreground">{t("Cuándo aviso")}</span>
        <span className="text-2xs leading-5 text-muted-foreground">
          {/*
            UN «CARGANDO» QUE NO TERMINA NUNCA es peor que no decir nada: la
            consulta ni siquiera se lanza sin sociedad elegida, así que el
            texto se quedaba ahí para siempre y parecía que el servidor no
            contestaba.
          */}
          {cartera.data?.politica
            ? resumenDeLaPolitica(cartera.data.politica)
            : !listo
              ? t("Se muestra al elegir una sociedad: cada una puede tener la suya.")
              : t("Cargando la política vigente…")}
        </span>
        {puedeEditar ? (
          <button
            type="button"
            onClick={() => setCadenciaAbierta(true)}
            className="ml-auto inline-flex items-center gap-1 text-2xs font-medium text-brand underline-offset-2 hover:underline"
          >
            <SlidersHorizontal className="h-3 w-3" aria-hidden="true" />
            {t("Cambiar cuándo aviso")}
          </button>
        ) : null}
      </div>

      <CadenciaDialog
        open={cadenciaAbierta}
        onOpenChange={setCadenciaAbierta}
        vigente={cartera.data?.politica}
      />

      {seleccionadas.length > 0 && puedeEditar ? (
        <div className="animate-row-in flex flex-wrap items-center gap-2 rounded-panel border bg-surface-sunken px-3 py-2">
          <span className="text-xs font-medium">
            {seleccionadas.length}{" "}{t("seleccionada(s)")}</span>
          <span className="text-2xs text-muted-foreground">
            {t("Se manda a cada una el aviso que le toca hoy, no uno genérico.")}</span>
          <Button
            size="sm"
            className="ml-auto"
            disabled={enviar.isPending}
            onClick={() => enviar.mutate()}
          >
            <Mail />
            {enviar.isPending ? "Enviando…" : "Enviar seguimiento"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMarcadas({})}>
            {t("Quitar selección")}</Button>
        </div>
      ) : null}

      {resultados ? (
        <ResultadosDelEnvio resultados={resultados} onCerrar={() => setResultados(null)} />
      ) : null}

      <WorkPanel
        title={t("Cartera por cobrar")}
        meta={
          <span className="flex flex-wrap items-center gap-1.5 text-2xs text-muted-foreground">
            {Object.entries(tramos).map(([tramo, t]) => (
              <Badge key={tramo} variant={tramo === "+90" ? "warning-soft" : "outline"}>
                {tramo}: {t.facturas} · {formatAmount(t.importe_usd)}
              </Badge>
            ))}
          </span>
        }
        actions={
          /*
            EL EDITOR DE TEXTOS, DESDE LA CARTERA Y NO DESDE UN MENÚ DE
            AJUSTES. Se entra a cambiar una palabra justo después de leer el
            aviso que se acaba de mandar, y ese momento es aquí.
          */
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPlantillasAbiertas(true)}
          >
            <Mail className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t("El texto de los avisos")}
          </Button>
        }
        footnote={cartera.data?.nota}
      >
        <DataState
          loading={cartera.isLoading}
          error={cartera.error}
          empty={!cartera.isLoading && filas.length === 0}
          /*
            TRES VACÍOS DISTINTOS Y NO UNO.

            «Todavía no hay sociedad elegida» NO es «no hay nada por cobrar»,
            y hasta ahora se leían igual: la pantalla decía «falta cargar
            ventas o gastos del mes» a alguien que sólo tenía que elegir
            arriba. Es el mismo `null` con dos significados del punto 7 — se
            ve en una captura y ningún test lo nota, porque la consulta ni
            siquiera se lanza y no hay error que contar.
          */
          emptyMessage={
            !listo ? t("Elige una sociedad arriba")
              : soloHoy ? t("Hoy no toca reclamar nada.")
                : t("No hay nada por cobrar.")}
          emptyHint={
            !listo
              ? t("La cartera es de una sociedad: desde aquí se mandan correos de reclamo, y reclamar la factura de otra es peor que no verla.")
              : soloHoy ? t("Quita el filtro para ver la cartera entera.")
                : undefined}
          onRetry={() => void cartera.refetch()}
          rows={8}
          columns={[4, 22, 14, 10, 12, 10, 12, 16]}
        >
          <div className="overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead className="min-w-[14rem]">{t("Cliente")}</TableHead>
                  <TableHead className="w-28">{t("Factura")}</TableHead>
                  <TableHead className="w-24">{t("Sociedad")}</TableHead>
                  <TableHead className="w-28">{t("Vence")}</TableHead>
                  <TableHead className="w-20 text-right">{t("Días")}</TableHead>
                  <TableHead className="w-28 text-right">{t("Importe")}</TableHead>
                  <TableHead className="w-28 text-right">{t("USD")}</TableHead>
                  <TableHead className="min-w-[12rem]">{t("Qué toca")}</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.map((d) => (
                  <Fila
                    key={d.id}
                    d={d}
                    marcada={Boolean(marcadas[d.id])}
                    puedeEditar={puedeEditar}
                    onMarcar={(v) => setMarcadas((m) => ({ ...m, [d.id]: v }))}
                    onIncobrable={() => setAIncobrable(d)}
                    onReactivar={() => revertir.mutate(d.id)}
                    onGestionar={() => gestionar.mutate({ id: d.id })}
                    onSoltar={() => soltar.mutate(d.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </DataState>
      </WorkPanel>

      <PlantillasDialog
        open={plantillasAbiertas}
        onOpenChange={setPlantillasAbiertas}
        puedeEditar={puedeEditar}
      />

      <ReasonDialog
        open={Boolean(aIncobrable)}
        onOpenChange={(abierto) => { if (!abierto) setAIncobrable(null) }}
        title={`Dar por incobrable la factura ${aIncobrable?.numero ?? ""}`}
        description={
          "Dejo de reclamarla y deja de contar como algo que esperas cobrar. Es " +
          "una decisión contable: lleva provisión y, en varios países, el " +
          "requisito de haber gestionado el cobro — que es lo que documenta el " +
          "histórico de esta factura. Se puede revertir."
        }
        confirmLabel={t("Darla por incobrable")}
        pending={marcarIncobrable.isPending}
        onConfirm={(motivo) => {
          if (aIncobrable) marcarIncobrable.mutate({ id: aIncobrable.id, motivo })
        }}
      />
    </div>
  )
}

/* ========================================================================== */

const ICONO_TOQUE = {
  antes: Clock,
  vispera: Clock,
  vencimiento: AlertTriangle,
  seguimiento: Mail,
  escalamiento: PhoneCall,
} as const

function Fila({
  d, marcada, puedeEditar, onMarcar, onIncobrable, onReactivar, onGestionar,
  onSoltar,
}: {
  d: Deudor
  marcada: boolean
  puedeEditar: boolean
  onMarcar: (v: boolean) => void
  onIncobrable: () => void
  onReactivar: () => void
  onGestionar: () => void
  onSoltar: () => void
}) {
  const Icono = d.toca_hoy ? ICONO_TOQUE[d.toca_hoy.tipo] : Clock
  const escala = d.toca_hoy?.tipo === "escalamiento"

  return (
    <TableRow className={cn(d.incobrable && "opacity-60")}>
      <TableCell>
        {/*
          NI LAS INCOBRABLES NI LAS QUE ESTÁN EN GESTIÓN se pueden marcar para
          el envío en lote. El servidor las omite igual y dice por qué, pero
          ofrecer la casilla sería prometer un envío que no va a salir: el
          contador marcaría doce y saldrían nueve sin entender cuáles.

          Para las que están en gestión el aviso se manda igual, uno a uno y
          eligiendo el toque: quien lo hace a mano sabe lo que hace.
        */}
        {puedeEditar && !d.incobrable && !d.en_gestion ? (
          <Checkbox
            checked={marcada}
            onCheckedChange={(v) => onMarcar(Boolean(v))}
            aria-label={`Seleccionar la factura de ${d.cliente ?? ""}`}
          />
        ) : null}
      </TableCell>

      {/*
        EL CORREO SE MUESTRA, NO SE PIDE. Cesar: «eliminar esa línea de la parte
        de cobranza y que sólo salga el botón para darle click a enviar».

        Aquí había un campo de texto por FILA pidiendo la dirección: en una
        cartera de 193 facturas, 193 campos vacíos en una tabla que se entra a
        LEER. Y el dato no se guardaba en ninguna parte, así que quien reclamaba
        en marzo lo volvía a teclear en abril y en mayo otra persona escribía uno
        distinto — un aviso a una dirección equivocada no rebota: el cliente
        simplemente no contesta y parece que no quiere pagar.

        Ahora vive en la ficha del cliente (migración 0024). Cuando falta, se
        DICE y se enlaza a su ficha, que es donde se arregla para siempre; no se
        ofrece un hueco que alguien rellenará otra vez el mes que viene.
      */}
      <TableCell className="font-medium">
        <span className="block truncate" title={d.cliente ?? ""}>{d.cliente}</span>
        {d.email_cobranza ? (
          <span className="block truncate text-2xs font-normal text-muted-foreground"
                title={d.email_cobranza}>
            {d.email_cobranza}
          </span>
        ) : (
          <Link
            to="/contabilidad/maestros/clientes"
            className="flex items-center gap-1 text-2xs font-normal text-warning-foreground underline-offset-2 hover:underline"
            title={`${d.cliente ?? "Este cliente"} no tiene correo de cobranza en su ficha. Sin él no se le puede mandar el aviso.`}
          >
            <MailWarning className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t("sin correo en su ficha")}
          </Link>
        )}
      </TableCell>

      {/*
        COMENTAR UNA FACTURA. Cesar lo pidió para las cuatro cosas, y ésta es la
        que más falta hace: desde esta pantalla se MANDAN correos de reclamo, y
        «a este cliente no le escribas todavía, está renegociando» es justo lo
        que hay que poder dejar dicho antes de que alguien pulse enviar.
      */}
      <TableCell className="code text-2xs">
        <span className="flex items-center gap-1">
          {d.numero}
          <BotonComentario
            ancla={{
              ambito: "factura",
              referencia: refFactura(d.id),
              rotulo: `${d.cliente ?? "Factura"} · ${d.numero}`,
            }}
          />
        </span>
      </TableCell>
      <TableCell className="text-2xs text-muted-foreground">{d.pais}</TableCell>

      <TableCell className="code text-2xs">
        {d.fecha_vencimiento ? formatDate(d.fecha_vencimiento) : (
          // SIN FECHA no es «vencida hace mil días». Ver el docstring.
          <span className="text-muted-foreground">{t("sin fecha")}</span>
        )}
      </TableCell>

      <TableCell className={cn("num", (d.dias_vencido ?? 0) > 90 && "text-warning")}>
        {d.dias_vencido === null ? "" : d.dias_vencido}
      </TableCell>

      {/*
        EL IMPORTE VA EN SU MONEDA, y el USD al lado. Reclamarle «USD 2.523,21»
        a un cliente mexicano cuya factura está en pesos le da la razón cuando
        conteste que ése no es el importe. El USD es para que el grupo sume.
      */}
      <TableCell className="num whitespace-nowrap">
        {formatAmount(d.importe)}{" "}
        <span className="text-2xs text-muted-foreground">{d.moneda}</span>
      </TableCell>
      <TableCell className="num">{formatAmount(d.importe_usd)}</TableCell>

      <TableCell>
        {d.incobrable ? (
          <Badge variant="outline" estado>
            <Ban />
            incobrable
          </Badge>
        ) : d.en_gestion ? (
          /*
            EN GESTIÓN GANA AL «TE TOCA HOY», y no lo tapa por estética: es que
            hoy NO le toca nada. La cadencia automática no le escribe mientras
            esté así, y enseñar el toque al lado sería decir que el sistema va
            a hacer algo que no va a hacer.
          */
          <span className="flex items-center gap-1.5 text-2xs">
            <Handshake className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span>
              {t("La estás hablando tú")}
              {d.en_gestion_desde ? ` · ${t("desde")} ${formatDate(d.en_gestion_desde)}` : ""}
              <span className="block text-muted-foreground">
                {d.en_gestion_nota || t("sin avisos automáticos")}
              </span>
            </span>
          </span>
        ) : d.toca_hoy ? (
          <span className={cn("flex items-center gap-1.5 text-2xs",
                              escala && "text-warning")}>
            <Icono className="h-3 w-3 shrink-0" aria-hidden="true" />
            {escala ? "Que el gerente llame" : d.toca_hoy.motivo}
          </span>
        ) : (
          <span className="text-2xs text-muted-foreground">
            {d.ultimo_contacto
              ? `Último aviso: ${formatDate(d.ultimo_contacto)}`
              : "Nada por ahora"}
          </span>
        )}
        {/*
          EL AVISO DE PROVISIÓN ES UN BADGE Y NO UNA FRASE. En una cartera donde
          73 de 193 facturas pasan de 180 días, la frase entera se repite 73
          veces y empuja la columna fuera de la pantalla. El badge dice lo mismo
          y el `title` guarda la frase para quien la necesite.
        */}
        {d.probable_incobrable && !d.incobrable ? (
          <Badge
            variant="outline"
            className="mt-0.5"
            title={t("Más de 180 días vencida: conviene decidir si se provisiona. No es una decisión tomada.")}
          >
            {t("+180 días")}</Badge>
        ) : null}
      </TableCell>

      <TableCell>
        {puedeEditar ? (
          d.incobrable ? (
            <Button size="xs" variant="ghost" onClick={onReactivar}>
              <Undo2 />
              {t("Reactivar")}</Button>
          ) : (
            <div className="flex flex-col items-start gap-0.5">
              {/*
                LOS TRES ESTADOS, EN EL ORDEN EN QUE SE RECORREN: normal →
                en gestión → incobrable. El escalón del medio es el que faltaba
                y el que cuesta un clic; el último sigue pidiendo motivo.
              */}
              {d.en_gestion ? (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={onSoltar}
                  title={t("Vuelvo a mandarle los recordatorios automáticos según su antigüedad.")}
                >
                  <Undo2 />
                  {t("Reanudar los avisos")}</Button>
              ) : (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={onGestionar}
                  title={t("Dejo de mandarle recordatorios automáticos porque lo estás hablando tú. La deuda sigue viva y sigue contando días.")}
                >
                  <Handshake />
                  {t("Pausar los avisos")}</Button>
              )}
              <Button
                size="xs"
                variant="ghost"
                onClick={onIncobrable}
                title={t("Decisión contable: lleva provisión y sale de lo que esperas cobrar. Te pido el motivo.")}
              >
                <Ban />
                {t("Darla por incobrable")}</Button>
            </div>
          )
        ) : null}
      </TableCell>
    </TableRow>
  )
}

/**
 * QUÉ PASÓ CON CADA ENVÍO, y el texto para copiar cuando no salió.
 *
 * Es la mitad que hace útil el «no se pudo enviar». Sin el texto a la vista,
 * un servidor sin SMTP convierte la pantalla en un botón que no hace nada; con
 * él, el cobrador copia y pega y la gestión sigue.
 */
function ResultadosDelEnvio({
  resultados, onCerrar,
}: {
  resultados: ResultadoSeguimiento[]
  onCerrar: () => void
}) {
  const { toast } = useToast()
  const fallidos = resultados.filter((r) => r.resultado !== "enviado")

  const copiar = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto)
      toast({ title: "Copiado" })
    } catch {
      toast({ variant: "warning", title: "No se pudo copiar",
              description: "Selecciónalo y cópialo a mano." })
    }
  }

  return (
    <WorkPanel
      title={t("Qué pasó con cada envío")}
      meta={
        <Badge variant={fallidos.length ? "warning-soft" : "success-soft"} estado>
          {fallidos.length ? <AlertTriangle /> : <Check />}
          {resultados.length - fallidos.length} de {resultados.length}
        </Badge>
      }
      actions={
        <Button size="xs" variant="ghost" onClick={onCerrar}>
          {t("Cerrar")}</Button>
      }
    >
      <ul className="divide-y">
        {resultados.map((r) => (
          <li key={r.invoice_id} className="px-2.5 py-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-xs font-medium">{r.cliente ?? r.invoice_id}</span>
              <Badge
                variant={r.resultado === "enviado" ? "success-soft" : "warning-soft"}
                estado
              >
                {r.resultado.replace(/_/g, " ")}
              </Badge>
              {r.destinatario ? (
                <span className="code text-2xs text-muted-foreground">{r.destinatario}</span>
              ) : null}
            </div>
            {r.detalle ? (
              <p className="mt-0.5 text-2xs leading-4 text-muted-foreground">{r.detalle}</p>
            ) : null}
            {r.resultado !== "enviado" && r.cuerpo ? (
              <div className="mt-1.5 rounded-md border bg-surface-sunken/50 p-2">
                <p className="text-2xs font-medium">{r.asunto}</p>
                <pre className="mt-1 whitespace-pre-wrap text-2xs leading-4 text-muted-foreground">
                  {r.cuerpo}
                </pre>
                <Button
                  size="xs"
                  variant="outline"
                  className="mt-1.5"
                  onClick={() => void copiar(`${r.asunto}\n\n${r.cuerpo}`)}
                >
                  <Copy />
                  {t("Copiar el texto")}</Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </WorkPanel>
  )
}

/**
 * La política de cobranza en una frase.
 *
 * Se arma con lo que el backend ya devuelve en `cartera.politica`, y sin
 * inventar campos: si una clave no viene, ese trozo de la frase no se dice. Una
 * política a medias contada como si estuviera completa es peor que no contarla —
 * quien lee «cada 7 días» y en realidad son 3 deja de mirar la columna.
 */
function resumenDeLaPolitica(p: Record<string, unknown>): string {
  const partes: string[] = []
  const dias = Number(p.dias_credito ?? p.terminos_pago_dias ?? NaN)
  if (Number.isFinite(dias)) partes.push(`vence a ${dias} días`)
  const previos = Array.isArray(p.avisos_previos) ? (p.avisos_previos as number[]) : []
  if (previos.length) partes.push(`aviso ${previos.join(" y ")} días antes`)
  if (p.aviso_el_dia) partes.push("y el día")
  const seguimiento = Array.isArray(p.dias_de_seguimiento) ? (p.dias_de_seguimiento as number[]) : []
  if (seguimiento.length) partes.push(`después, los ${seguimiento.map((d) => NOMBRE_DIA[d] ?? String(d)).join(" y ")}`)
  const escala = Number(p.escalar_a_los_dias ?? NaN)
  if (Number.isFinite(escala)) partes.push(`escala al gerente a los ${escala}`)
  return partes.length ? partes.join(" · ") : "La que trae el sistema por defecto."
}

/** «2026-11» → «nov 2026». */
function nombreDelMes(aaaaMm: string): string {
  const [a, m] = aaaaMm.split("-")
  const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"]
  return `${meses[Number(m) - 1] ?? m} ${a}`
}

const NOMBRE_DIA: Record<number, string> = {
  1: "lunes", 2: "martes", 3: "miércoles", 4: "jueves", 5: "viernes", 6: "sábados", 7: "domingos",
}
